<div class="container-fluid" appPadding>
  <div class="row">
    <div class="col-12">
      <h4>Importing {{ displayType }}</h4>

      <form #f="ngForm" class="form-container" (ngSubmit)="onSubmit(f)">
        <div>
          <p>
            You are about to import {{ displayType }}. Please select the CSV file you would like to upload and import.
          </p>

          <ng-container *ngIf="type === 'contractors' || type === 'employees' || type === 'toolbox-talks-templates' || type === 'vendors'|| type === 'assets' || type === 'hazards' || type === 'asset-types'">
            <button
              mat-raised-button
              type="button"
              (click)="onDownloadImportTemplate()"
              color="secondary"
              class="my-1 ml-0 mr-3">
              <mat-icon>cloud_download</mat-icon>Download CSV Template
            </button>
          </ng-container>

          <button
            mat-raised-button
            type="button"
            (click)="onTriggerFileSelector($event)"
            color="primary"
            class="my-1 ml-0 mr-3">
            Select &amp; Upload CSV File
          </button>

          <span *ngIf="selectedFile">{{ selectedFile.name }}</span>

          <input type="file" (change)="onFileSelected($event)" style="display: none;" #fileInput/>

          <p *ngIf="selectedFile && !(possible_headers.length && sample_data.length)" appMarginTop>
            Please wait while we upload and analyse the file.
          </p>
        </div>

        <ng-container *ngIf="possible_headers.length && sample_data.length">
          <p appMarginTop>
            Map the columns below to its corresponding fields in the <b>sample data</b> table below.
            If you map more than one column to the same field, the data will be concatenated.
          </p>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'contractors'">
            Example: "Column A" and "Column B" columns can be combined into one field called "Full Name" if you map "Full Name" to both columns. <br><br>
            The <b>Entity Name</b>, <b>Full Name</b>, <b>Email</b>, <b>Country Code</b> and <b>Mobile</b> columns are required. For the Country Code, enter the country abbreviation such as "NZ" for New Zealand or "AU" for Australia, not the international dialing codes like "64" or "61".
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'employees'">
            Example: "Column A" and "Column B" columns can be combined into one field called "Full Name" if you map "Full Name" to both columns. <br><br>
            The <b>Full Name</b>, <b>Email</b>, <b>Country Code</b> and <b>Mobile</b> columns are required. For the Country Code, enter the country abbreviation such as "NZ" for New Zealand or "AU" for Australia, not the international dialing codes like "64" or "61".
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'user-documents'">
            Example: "Column A" and "Column B" columns can be combined into one field called "Document Name" if you map "Document Name" to both columns. <br><br>
            The <b>Email Address</b> and <b>Document Name</b> columns are required.
            The mapped <b>Document Name</b> column can contain comma-separated <b>Document Names</b> to import multiple documents in one row.
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'sites'">
            Example: "Column A" and "Column B" columns can be combined into one field called "Name" if you map "Name" to both columns. <br><br>
            The <b>Name</b> and <b>Location</b> columns are required.
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'hazards'">
            Example: "Column A" and "Column B" columns can be combined into one field called "Hazard/Risk" if you map "Hazard/Risk" to both columns. <br><br>
            The <b>Hazard/Risk</b>, <b>Description of Risk/Harm</b> and <b>Status</b> columns are required.
            The optional <b>Initial Risk Assessment Likelihood</b>, <b>Initial Risk Assessment Severity</b>, <b>Residual Risk Assessment Likelihood</b> and <b>Residual Risk Assessment Severity</b> columns accept a number from 1 to 5. Any missing or invalid value defaults to 2.
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'toolbox-talks-templates'">
            <b>Note:</b> If Template Type is is left blank, it will default to template. <br><br>
            The <b>Name</b> and <b>Agenda</b> columns are required. The optional <b>Template Type</b> column only accepts "template" and "snippet" values. It defaults to "template".
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'vendors'">
            The <b>Name</b>, <b>Email</b>, <b>Country Code</b> and <b>Phone Number</b> columns are required. For the Country Code, enter the country abbreviation such as "NZ" for New Zealand or "AU" for Australia, not the international dialing codes like "64" or "61".
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'asset-types'">
            The <b>Asset Type Name</b> column is required: Top-level type names must be unique within your account, and subtype names must be unique within their parent type. Existing names are matched (case-insensitive) and reused rather than duplicated. <br><br>
            The optional <b>Parent Asset Type Name</b> column determines the hierarchy: leave it blank to import the row as a top-level asset type, or enter the name of its parent asset type to import the row as a subtype. The parent type will be created automatically if it does not already exist.
          </div>

          <div class="alert alert-info" appMarginTop *ngIf="type === 'assets'">
            The <b>Name</b> and <b>Asset Type</b> columns are required.

            If the provided <b>Asset Type</b> or <b>Asset Subtype</b> does not already exist within your account, it will automatically be created during the import process. Existing Asset Types and Subtypes will be matched automatically where possible. <br><br>

            If not mapped, the following default values will be applied:
            <b>Status</b> = "Active/Operational",
            <b>Owner Type</b> = "Owned" and
            <b>Condition Rating</b> = "Fair". <br><br>

            The <b>Cost Amount</b> column must contain numeric values only and cannot be negative. <br><br>

            The <b>Noise Rating</b> column must contain a whole number between 0 and 140. <br><br>

            The <b>Visible on Mobile app?</b>, <b>Movable Asset?</b> and <b>Safety Critical?</b> columns only accept Yes/No values.
          </div>

          <p appMarginTop>
            Rows without data or invalid data in these columns will be ignored.
          </p>

          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="thead-light">
                <tr>
                  <th scope="col" *ngFor="let header of possible_headers">
                    {{ header }}
                  </th>
                </tr>
                <tr>
                  <th scope="col" *ngFor="let header of possible_headers; let i = index">
                    <mat-form-field appearance="outline" subscriptSizing="dynamic">
                      <mat-label>Map Field</mat-label>
                      <mat-select placeholder="Map Field" [(ngModel)]="mapped_fields[i]" name="header_{{ i }}">
                        <mat-option value="-1">-- Ignore --</mat-option>
                        <mat-option *ngFor="let field of importable_fields; let j = index" [value]="j">
                          {{ field }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr *ngFor="let row of sample_data">
                  <td *ngFor="let col of row">
                    {{ col.length > 32 ? (col | slice: 0:32) + '...' : col }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div appMarginTop>
            <div>
              <mat-slide-toggle [(ngModel)]="first_row_headers" name="first_row_headers">
                Treat the first row as headers.
              </mat-slide-toggle>
            </div>

            <div *ngIf="type === 'contractors'">
              <mat-slide-toggle [(ngModel)]="first_row_admin" name="first_row_admin" (change)="onToggleCompanyAdmin()">
                The first person is the {{ utils.getLangTerm('contractors.singular', 'Contractor') }} Administrator; the rest are Employees.
              </mat-slide-toggle>
            </div>

            <div *ngIf="type === 'contractors' || type === 'employees'">
              <mat-slide-toggle [(ngModel)]="send_notifications" name="send_notifications">
                Send email notifications.
              </mat-slide-toggle>
            </div>

            <button mat-raised-button type="submit" color="primary" appMarginTop [disabled]="!isValidMapping() || is_importing">
              Import {{ displayType }}
            </button>

            <span *ngIf="is_importing">
              You will get an email when your data is imported.
            </span>
          </div>
        </ng-container>
      </form>
    </div>
  </div>
</div>
